/**
 *
 * FlexDropdown
 *
 */

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
// import PropTypes from 'prop-types';
// import styled from 'styled-components';

import { FormattedMessage } from 'react-intl';
import messages from './messages';
import styles from './styles.module.css';

import { Icon } from 'semantic-ui-react';
import FlexBox from 'components/common/FlexBox';
import FlexIcon from 'components/common/FlexIcon';
import FlexPopup from 'components/common/FlexPopup';
import { isMobileOnly } from 'react-device-detect';

import ColorUtils from 'utils/ColorUtils';

function FlexDropdown(props) {
  let {
    gutter = 10,
    value,
    clickable = true,
    id,
    icon,
    style,
    text,
    subtext,
    searchable,
    disabled,
    material,
    fluid,
    showCaret,
    showKabob,
    fontawesome,
    transparent,
    shrinkable,
    color,
    primary,
    guttered,
    vstretch,
    hstretch,
    placeholder,
    background,
    subtextMatchValue,
    defaultValue,
    hollow,
    rightGutter,
    bottomGutter,
    leftGutter,
    topGutter,
    fill,
    onClick,
    onChange,
    loading,
    compact,
    options,
    content,
    subcontent,
    maxMenuHeight,
    overlay,
    searchAutofocus,
    activated,
    secondary,
    tertiary,
    onSurfaceColor = 'var(--background-color)',
  } = props;

  if (!onClick && onChange) {
    onClick = onChange;
  }

  /*
    If the text is not set, but the subtext is, then set the text to the subtext and remove the subtext.
    This is to ensure that the dropdown always has text to display.
  */
  if (text && !subtext && !subtextMatchValue) {
    subtext = text;
    text = undefined;
  }

  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const [search, setSearch] = useState('');
  const [stackIndex, setStackIndex] = useState(0);
  const [, updateState] = React.useState();
  const forceUpdate = React.useCallback(() => updateState({}), []);
  const localData = useRef({
    open: false,
  });
  const searchRef = useRef(null);

  let elemStyle = style ? { ...style } : {};
  if (rightGutter || guttered) {
    elemStyle.marginRight = `${gutter}px`;
  }

  if (bottomGutter || guttered) {
    elemStyle.marginBottom = `${gutter}px`;
  }

  if (leftGutter || guttered) {
    elemStyle.marginLeft = `${gutter}px`;
  }

  if (topGutter || guttered) {
    elemStyle.marginTop = `${gutter}px`;
  }

  if (vstretch) {
    elemStyle.height = '100%';
    elemStyle.overflowY = 'auto';
  }

  if (hstretch || fluid) {
    elemStyle.width = '100%';
  }

  let classes = `${styles.flexDropdown}`;
  if (primary) {
    classes += ` ${styles.primary}`;
  }
  if (secondary) {
    classes += ` ${styles.secondary}`;
  }
  if (tertiary) {
    classes += ` ${styles.tertiary}`;
  }
  if (color) {
    classes += ` ${styles[color]}`;
  }
  if (loading) {
    classes += ` ${styles.loading}`;
  }
  if (showCaret === false) {
    classes += ` ${styles.noCaret}`;
  }
  if (showKabob === false) {
    classes += ` ${styles.noKabob}`;
  }
  if (disabled) {
    classes += ` ${styles.dropdownDisabled}`;
  }
  if (!text && !icon) {
    classes += ` ${styles.standalone}`;
  }
  if (transparent) {
    classes += ` ${styles.transparent}`;
  }
  if (props.className) {
    classes = ` ${props.className} ` + classes;
  }
  if (searchAutofocus) {
    classes += ` ${styles.autofocusInUse}`;
  }
  if (hollow) {
    classes += ` ${styles.hollow}`;
  }
  if (activated) {
    classes += ` ${styles.activated}`;
  }

  useEffect(() => {
    let clickListener = (e) => {
      const target = e.target;
      const contentContainer = target?.closest(`.content`);
      if (contentContainer) {
        return;
      }

      if (
        localData.current.open &&
        dropdownRef.current &&
        dropdownRef.current !== e.target &&
        !dropdownRef.current.contains(e.target) &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
        localData.current.open = false;
      }
    };

    let resizeListener = (e) => {
      forceUpdate();
    };

    window.addEventListener('pointerup', clickListener);
    window.addEventListener('resize', resizeListener);
    return () => {
      window.removeEventListener('pointerup', clickListener);
      window.removeEventListener('resize', resizeListener);
    };
  }, []);

  const handleOnClick = (e) => {
    if (isMobileOnly || clickable) {
      e.stopPropagation();
      if (e.target !== menuRef.current && (!menuRef.current || !menuRef.current.contains(e.target))) {
        setOpen(!open);
        if (!open && searchAutofocus && searchRef && searchRef.current) {
          searchRef.current.focus();
        }
        if (!window._globaPopupStackIndex) {
          window._globaPopupStackIndex = 1;
        } else {
          window._globaPopupStackIndex++;
        }

        if (props.onOpen) {
          props.onOpen();
        }

        setStackIndex(window._globaPopupStackIndex);
        localData.current.open = !localData.current.open;
        if (localData.current.open) {
          // Ensures that the close animation does not occur on initialization of the component
          localData.current.openedOccurred = true;
        }
        forceUpdate();
      }
    }
  };

  const handlePointerEnter = (e) => {
    if (
      (dropdownRef.current && dropdownRef.current.contains(e.target)) ||
      (menuRef.current && menuRef.current.contains(e.target))
    ) {
      if (
        (!isMobileOnly &&
          dropdownRef.current &&
          dropdownRef.current.contains(e.target) &&
          props.hoverable !== false &&
          !clickable) ||
        (menuRef.current && menuRef.current.contains(e.target))
      ) {
        clearTimeout(localData.current.closeMenuTimeout);
        localData.current.closeMenuTimeout = undefined;
        setOpen(true);
        if (searchAutofocus && searchRef && searchRef.current) {
          searchRef.current.focus();
        }
        localData.current.open = true;
        localData.current.openedOccurred = true;
        forceUpdate();

        if (props.onOpen) {
          props.onOpen();
        }

        if (!window._globaPopupStackIndex) {
          window._globaPopupStackIndex = 1;
        } else {
          window._globaPopupStackIndex++;
        }

        setStackIndex(window._globaPopupStackIndex);
      }
    }
  };

  const handlePointerLeave = (e) => {
    if (!isMobileOnly && !localData.current.clicked) {
      localData.current.closeMenuTimeout = setTimeout(() => {
        localData.current.open = false;
        setOpen(false);
      }, 300);

      localData.current.openMenuDeferred = undefined;
    }
  };

  let menuElement;
  let entries;
  let selectedEntryText;
  let selectedEntrySubtext;
  let selectedEntryIcon;

  if (options) {
    let filteredOptions = options.filter((option) => {
      if (search.length === 0) {
        if (option.searchOnly) {
          return false;
        } else {
          return true;
        }
      } else {
        if (option.type === 'separator' || option.type === 'heading') {
          return false;
        } else {
          if (option.text && option.text.toLowerCase().indexOf(search.toLowerCase()) > -1) {
            return true;
          }
        }
      }
    });

    let keyInstances = {};
    if (filteredOptions.length === 0) {
      entries = [
        <div key="splash" className={styles.noResultsSplash}>
          No Matching Results
        </div>,
      ];
    } else {
      entries = filteredOptions.map((option, index) => {
        if (option.visible !== false) {
          const handleClick = (e) => {
            if (option.disabled) {
              return;
            }
            if (option.interactive !== false) {
              e.stopPropagation();
              if (onClick) {
                let response;
                if (props.semantic) {
                  response = onClick(e, {
                    value: option.value,
                  });
                } else {
                  response = onClick(option);
                }

                if (response !== false) {
                  setOpen(false);
                  setSearch('');
                  localData.current.open = false;
                }
              } else if (option.onClick) {
                let response = option.onClick();
                if (response !== false) {
                  setOpen(false);
                  setSearch('');
                  localData.current.open = false;
                }
              }
            }
          };

          if (option.type === 'heading') {
            return (
              <div key={`heading_${index}`} className={styles.flexDropdownHeading}>
                {option.text}
              </div>
            );
          } else if (option.type === 'separator') {
            return <div key={`sep_${index}`} className={styles.flexDropdownSeparator} />;
          } else {
            const { suggested, context } = option;
            let entryStyle = {};
            if (suggested) {
              entryStyle.color = 'var(--app-blue)';
            }

            if (context) {
              if (context === 'delete') {
                entryStyle.color = 'var(--app-red)';
              }
              if (context === 'confirm') {
                entryStyle.color = 'var(--app-blue)';
              }
            }

            if (option.value !== undefined && value !== undefined && option.value === value && !props.text) {
              selectedEntryText = option.text;
              if (props.showValueSubtext && !subtext && option.subtext) {
                selectedEntrySubtext = option.subtext;
              }
            }

            if (
              option.value !== undefined &&
              value !== undefined &&
              option.value === value &&
              !subtext &&
              subtextMatchValue
            ) {
              selectedEntrySubtext = option.text;
            }

            if (option.value !== undefined && value !== undefined && option.value === value && !icon) {
              selectedEntryIcon = option.icon;
            }

            if (
              option.value !== undefined &&
              value === undefined &&
              defaultValue &&
              defaultValue === option.value &&
              !text
            ) {
              selectedEntryText = option.text;
              selectedEntryIcon = option.icon;
            }

            let shortcutStyle = {};
            if (option.shortcut && option.shortcutContext) {
              if (option.shortcutContext === 'positive') {
                shortcutStyle.background = 'var(--app-primary-color-hover)';
                shortcutStyle.color = 'white';
                shortcutStyle.border = 'none';
              }
            }

            let selected = false;
            let entryDisabled = false;
            let compareValue = value !== undefined ? value : defaultValue;

            if (option.value !== undefined && compareValue !== undefined) {
              if (typeof value === 'string') {
                selected = option.value === compareValue;
              } else if (Array.isArray(compareValue)) {
                selected = compareValue.indexOf(option.value) > -1;
              } else if (typeof compareValue === 'boolean') {
                selected = compareValue === option.value;
              }
            }

            if (option.disabled) {
              entryDisabled = true;
            }

            if (selected) {
              entryStyle.color = 'var(--background-text-color)';
            }

            let entryKey = option.key || option.value || option.text || option.subtext;
            if (!entryKey) {
              // If this debugger has hit,
              // then It's whomever's responsibility to see this to fix the missing key rendering issue.
              debugger;
            } else {
              if (!keyInstances[entryKey]) {
                keyInstances[entryKey] = 1;
              } else {
                let _origKey = entryKey;
                let count = keyInstances[_origKey];
                entryKey = `${_origKey}_flex_dropdown_entry_${count + 1}`;
                keyInstances[_origKey] = count + 1;
              }
            }

            let iconStyle = {};

            if (option.background) {
              entryStyle.background = option.background;
              entryStyle.color = ColorUtils.isColorDark(option.background)
                ? ColorUtils.brighten(option.background, 70)
                : ColorUtils.darken(option.background, 70);

              iconStyle.color = entryStyle.color;
              iconStyle.opacity = 1;
            }

            return (
              <div key={`default_${index}`} className={`${styles.flexDropdownEntryContainer} ${option.content ? styles.hasCustomContent : ''}`}>
                <FlexBox
                  style={entryStyle}
                  row
                  align="center"
                  handlers={{ onClick: handleClick }}
                  key={entryKey}
                  className={`${styles.flexDropdownEntry} ${selected ? styles.entrySelected : ''} ${entryDisabled ? styles.entryDisabled : ''} ${option.interactive === false ? styles.nointeraction : ''}`}
                >
                  {option.icon &&
                    (option.material ? (
                      <div
                        className={`${styles.flexDropdownEntryIcon} ${styles.material} material-icons`}
                        style={iconStyle}
                      >
                        {option.icon}
                      </div>
                    ) : (
                      <Icon className={styles.flexDropdownEntryIcon} name={option.icon} style={iconStyle} />
                    ))}
                  <FlexBox flex={1} column className={'content'}>
                    {option.text}
                    {option.content}
                    {option.subcontent}
                    {!option.subcontent && option.subtext && (
                      <div className={`${styles.entrySubtext} ${selected ? styles.entrySelected : ''}`}>
                        {option.subtext}
                      </div>
                    )}
                  </FlexBox>
                  {option.shortcut && (
                    <div style={shortcutStyle} className={styles.entryShortcut}>
                      {option.shortcut}
                    </div>
                  )}
                </FlexBox>
              </div>
            );
          }
        }
      });
    }

    let menuStyle = {};
    if (maxMenuHeight) {
      menuStyle.maxHeight = maxMenuHeight;
    }
    if (dropdownRef.current) {
      let bounds = dropdownRef.current.getBoundingClientRect();
      menuStyle.top = bounds.top + bounds.height;
      menuStyle.left = bounds.left;
      menuStyle.minWidth = bounds.width;
      if (menuRef.current) {
        let menuBounds = menuRef.current.getBoundingClientRect();
        menuStyle.left = Math.min(bounds.left, window.innerWidth - menuBounds.width - 10);
        menuStyle.top = Math.min(window.innerHeight - menuBounds.height - 10, bounds.top + bounds.height);
      }

      if (overlay) {
        menuStyle.top = bounds.top;
      }
    }

    if (!open) {
      // menuStyle.display = 'none';
    } else {
      menuStyle.zIndex = stackIndex;
    }

    menuElement = (
      <div
        ref={menuRef}
        style={menuStyle}
        className={`${styles.flexDropdownMenu} ${open ? styles.menuOpen : localData.current.openedOccurred ? styles.menuClosed : styles.menuInit}`}
      >
        {(searchable || props.search) && (
          <FlexBox align="center" className={styles.flexDropdownSearchContainer}>
            <Icon name="search" />
            <input
              ref={searchRef}
              placeholder={props.searchPlaceholder ? props.searchPlaceholder : 'Search...'}
              className={styles.flexDropdownSearch}
              onChange={(e) => setSearch(e.target.value)}
              value={search}
            />
          </FlexBox>
        )}
        <FlexBox column className={styles.menuPanel}>
          {entries}
        </FlexBox>
      </div>
    );
  }

  let dropdownText = text;
  let dropdownIcon = icon;
  let dropdownSubtext = subtext;
  if (selectedEntryText) {
    dropdownText = selectedEntryText;
  }
  if (selectedEntrySubtext) {
    dropdownSubtext = selectedEntrySubtext;
  }
  if (selectedEntryIcon) {
    dropdownIcon = selectedEntryIcon;
  }

  let isPlaceholder = false;
  if (!dropdownText) {
    if (placeholder) {
      dropdownText = placeholder;
      isPlaceholder = true;
    }
  }

  let textStyle = {};
  let subtextStyle = {};
  let iconStyle = {};
  if (background) {
    elemStyle.background = background;
    elemStyle.color = ColorUtils.isColorDark(background) ? 'white' : '#333';
  }

  if (props.textStyle) {
    textStyle = { ...props.textStyle };
  }

  if (props.subtextStyle) {
    subtextStyle = { ...props.subtextStyle };
  }

  if (props.iconStyle) {
    iconStyle = { ...props.iconStyle };
  }

  if (onSurfaceColor) {
    textStyle.background = onSurfaceColor;
  }

  if (dropdownText && !dropdownSubtext) {
    dropdownSubtext = dropdownText;
    dropdownText = undefined;
  }

  let dropdownContent = <div />;
  if (!showKabob) {
    dropdownContent = (
      <div
        tabIndex="0"
        ref={dropdownRef}
        className={classes}
        onClick={handleOnClick}
        onMouseEnter={handlePointerEnter}
        onMouseLeave={handlePointerLeave}
        style={elemStyle}
      >
        <FlexBox hstretch row align="center" flex={1} style={{ zIndex: 1 }}>
          {dropdownIcon &&
            (material !== true ? (
              <Icon
                style={iconStyle}
                className={`${styles.dropdownIcon} ${(text && text.length > 0) || (subtext && subtext.length > 0) ? '' : styles.standalone}`}
                name={dropdownIcon}
              />
            ) : (
              <div
                style={iconStyle}
                className={`${styles.dropdownIcon} ${(text && text.length > 0) || (subtext && subtext.length > 0 && props.iconOnly !== true) ? '' : styles.standalone} material-icons`}
              >
                {dropdownIcon}
              </div>
            ))}
          <FlexBox flex={1} hstretch align="flex-start" column>
            {content}
            {!content && props.iconOnly !== true && dropdownText && (
              <div
                style={textStyle}
                className={`${styles.dropdownText} ${!dropdownIcon ? styles.textOnly : ''} ${isPlaceholder ? styles.placeholder : ''} ${activated ? styles.activated : ''}`}
              >
                {dropdownText}
              </div>
            )}
            {subcontent}
            {!content && !subcontent && dropdownSubtext && props.iconOnly !== true && (
              <div style={subtextStyle} className={`${styles.dropdownSubtext} ${!text ? styles.subtextOnly : ''}`}>
                {dropdownSubtext}
              </div>
            )}
          </FlexBox>
          {showCaret !== false && (
            <div className={styles.dropdownCaret}>
              <div
                className={`${styles.dropdownCaretIcon} ${text && text.length > 0 && !compact ? '' : styles.standalone} material-icons`}
              >
                {props.caretIcon ? props.caretIcon : 'arrow_drop_down'}
              </div>
            </div>
          )}
        </FlexBox>
        {document.getElementById('drag_drop_overlay') &&
          menuElement &&
          ReactDOM.createPortal(menuElement, document.getElementById('drag_drop_overlay'))}
      </div>
    );
  } else {
    dropdownContent = (
      <div
        tabIndex="0"
        ref={dropdownRef}
        onClick={handleOnClick}
        onMouseEnter={handlePointerEnter}
        onMouseLeave={handlePointerLeave}
        style={elemStyle}
      >
        <FlexBox className={styles.flexFormHeader} row align="center" style={{ cursor: 'pointer' }}>
          <FlexIcon name="more_vert" material style={{ fontSize: 24 }} />
        </FlexBox>
        {document.getElementById('drag_drop_overlay') &&
          menuElement &&
          ReactDOM.createPortal(menuElement, document.getElementById('drag_drop_overlay'))}
      </div>
    );
  }

  if (props.tooltip) {
    return (
      <FlexPopup
        type="tooltip"
        position={props.tooltipPosition ? props.tooltipPosition : 'bottom middle'}
        content={props.tooltip}
      >
        {dropdownContent}
      </FlexPopup>
    );
  } else {
    return dropdownContent;
  }
}

FlexDropdown.propTypes = {};

export default FlexDropdown;
